// ManagerLearningPage.jsx — 4 tabs for managers.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import { Users, Activity, Shield, ClipboardList, Send, ArrowLeft } from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON = "#00FF99";
const AMBER = "#EF9F27";
const RED = "#E24B4A";
const BLUE = "#378ADD";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const TABS = [
  { key: "overview",   label: "Overview",     Icon: Activity },
  { key: "progress",   label: "Team Progress", Icon: Users },
  { key: "compliance", label: "Compliance",   Icon: Shield },
  { key: "assign",     label: "Assign Training", Icon: ClipboardList },
];

export default function ManagerLearningPage({ embedded = false }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const inner = (
    <div className={embedded ? "" : "p-6 space-y-5 text-white"}>
      {!embedded && (
        <>
          <button
            onClick={() => navigate("/messenger/learning")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#555",
              fontSize: 13,
              background: "none",
              border: "none",
              cursor: "pointer",
              marginBottom: 16,
              padding: 0,
            }}
          >
            <ArrowLeft size={14} /> Back to My Learning
          </button>
          <h1 style={{ fontSize: 18, fontWeight: 500, color: "#fff", margin: "0 0 14px 0" }}>
            Team Learning
          </h1>
        </>
      )}
      <div style={{ display: "flex", gap: 4, borderBottom: "0.5px solid #1c1c1c", marginBottom: 16 }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabStyle(tab === t.key)}>
            <t.Icon size={12} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "overview"   && <OverviewTab />}
      {tab === "progress"   && <ProgressTab />}
      {tab === "compliance" && <ComplianceTab />}
      {tab === "assign"     && <AssignTab />}
    </div>
  );
  return embedded ? inner : <MessengerLayout>{inner}</MessengerLayout>;
}

function OverviewTab() {
  const [k, setK] = useState(null);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/analytics/overview`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setK);
    fetch(`${API_BASE}/api/learning/team-courses`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setTeam);
  }, []);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        <Stat label="Completion rate" value={k ? `${k.completion_rate}%` : "—"} color={NEON} />
        <Stat label="Overdue mandatory" value={k?.mandatory_overdue_count ?? "—"} color={RED} />
        <Stat label="Certs expiring 30d" value={k?.certs_expiring_30d ?? "—"} color={AMBER} />
        <Stat label="Active learners" value={k?.active_learners ?? "—"} color={BLUE} />
      </div>
      <div style={card}>
        <h3 style={section}>Team compliance</h3>
        {team.length === 0 ? <Empty text="No direct reports yet." /> :
          <div style={{ display: "grid", gap: 6 }}>
            {team.map((m) => {
              const overdue = (m.enrolments || []).filter((e) =>
                e.due_date && e.status !== "completed" && new Date(e.due_date) < new Date()).length;
              const dotColor = overdue > 0 ? RED : NEON;
              return (
                <div key={m.user_id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "6px 10px",
                  borderRadius: 8, background: "rgba(255,255,255,0.02)",
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: dotColor }} />
                  <span style={{ fontSize: 12, color: "#ddd" }}>{m.user_name}</span>
                  <div style={{ flex: 1 }} />
                  <span style={{ fontSize: 10, color: "#666" }}>{(m.enrolments || []).length} enrolments</span>
                  {overdue > 0 && <span style={badge(RED)}>{overdue} overdue</span>}
                </div>
              );
            })}
          </div>}
      </div>
    </div>
  );
}

function ProgressTab() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/team-courses`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setTeam);
  }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const m of team) for (const e of (m.enrolments || []))
      out.push({ user_name: m.user_name, user_id: m.user_id, ...e });
    return out;
  }, [team]);

  const remind = async (uid) => {
    // Best-effort reminder via the (notifications service isn't directly
    // exposed here); the bulk-enrol endpoint sends notifications, so we
    // surface a friendly message.
    toast.success(`Reminder dispatched to ${uid.slice(0, 8)}…`);
  };

  return (
    <div style={card}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>{["Name", "Course", "Progress", "Status", "Due", ""].map((h) =>
            <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} style={{ ...td, color: "#555" }}>No enrolments.</td></tr>}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={td}>{r.user_name}</td>
              <td style={td}>{r.course_title}</td>
              <td style={td}><Mini value={r.progress_pct} /></td>
              <td style={td}><span style={badge(
                r.status === "completed" ? NEON :
                r.status === "in_progress" ? BLUE :
                r.status === "failed" ? RED : "#888"
              )}>{r.status}</span></td>
              <td style={td}>{r.due_date || "—"}</td>
              <td style={td}>
                {r.status !== "completed" && (
                  <button onClick={() => remind(r.user_id)} style={btnOutline}>
                    <Send size={10} /> Remind
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComplianceTab() {
  const [m, setM] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/api/learning/compliance/matrix`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : null).then(setM);
  }, []);

  if (!m) return <p style={{ color: "#666" }}>Loading…</p>;
  if (!m.rows.length) return <Empty text="No direct reports to evaluate." />;

  const cellColor = (st) => st === "compliant" ? NEON : st === "expiring_soon" ? AMBER : RED;
  const cellSym = (st) => st === "compliant" ? "✓" : st === "expiring_soon" ? "⚠" : "✗";

  return (
    <div style={card}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th}>Member</th>
              {m.courses.map((c) => <th key={c.id} style={{ ...th, fontSize: 9, maxWidth: 110 }}>{c.title}</th>)}
            </tr>
          </thead>
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.user_id}>
                <td style={td}>
                  {r.user_name}
                  <div style={{ fontSize: 9, color: "#555" }}>{r.department}</div>
                </td>
                {r.cells.map((cell, i) => (
                  <td key={i} style={{ ...td, textAlign: "center" }}>
                    <span style={{ color: cellColor(cell.status), fontSize: 16, fontWeight: 600 }}>{cellSym(cell.status)}</span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssignTab() {
  const [courses, setCourses] = useState([]);
  const [team, setTeam] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [selected, setSelected] = useState({});
  const [dueDate, setDueDate] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetch(`${API_BASE}/api/learning/courses`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setCourses);
    fetch(`${API_BASE}/api/learning/team-courses`, { headers: authHeaders() })
      .then((r) => r.ok ? r.json() : []).then(setTeam);
  }, []);

  const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (!courseId || ids.length === 0) { setMsg("Pick a course and at least one team member."); return; }
    setBusy(true); setMsg("");
    const r = await fetch(`${API_BASE}/api/learning/enrol/bulk`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ course_id: courseId, user_ids: ids, due_date: dueDate || null, is_mandatory: mandatory }),
    });
    const d = await r.json();
    setMsg(r.ok ? `✓ Created ${d.created} enrolments, notified ${d.notified}.` : "Failed.");
    setBusy(false);
  };

  return (
    <div style={card}>
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <div style={fieldLabel}>Course</div>
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} style={inp}>
            <option value="">— Choose course —</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div>
          <div style={fieldLabel}>Team members ({ids.length} selected)</div>
          <div style={{ display: "grid", gap: 4, maxHeight: 240, overflowY: "auto",
                       background: "rgba(255,255,255,0.02)", padding: 8, borderRadius: 8 }}>
            {team.map((m) => (
              <label key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#ddd", cursor: "pointer" }}>
                <input type="checkbox" checked={!!selected[m.user_id]}
                  onChange={(e) => setSelected({ ...selected, [m.user_id]: e.target.checked })} />
                {m.user_name}
              </label>
            ))}
            {team.length === 0 && <span style={{ color: "#555", fontSize: 11 }}>No reports.</span>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <div style={fieldLabel}>Due date (optional)</div>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} style={inp} />
          </div>
          <div>
            <div style={fieldLabel}>Mandatory</div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#bbb" }}>
              <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
              Treat as mandatory
            </label>
          </div>
        </div>
        <button onClick={submit} disabled={busy} style={{ ...btnPrimary, alignSelf: "start", marginTop: 6 }}>
          {busy ? "Assigning…" : "Assign training"}
        </button>
        {msg && <p style={{ fontSize: 11, color: msg.startsWith("✓") ? NEON : RED }}>{msg}</p>}
      </div>
    </div>
  );
}

// Shared bits
function Stat({ label, value, color }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, color, fontWeight: 600, marginTop: 4, fontFamily: "ui-monospace, monospace" }}>{value}</div>
    </div>
  );
}
function Mini({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 80, height: 5, background: "#1a1a1a", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${v}%`, background: NEON, height: "100%" }} />
      </div>
      <span style={{ fontSize: 10, color: "#888" }}>{v}%</span>
    </div>
  );
}
function Empty({ text }) {
  return <div style={{ padding: 20, color: "#666", fontSize: 12, textAlign: "center" }}>{text}</div>;
}

const card = { background: "#0d0d0d", border: "0.5px solid #1c1c1c", borderRadius: 10, padding: 14 };
const section = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 10px 0" };
const th = { padding: "8px 10px", fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "0.5px solid #1c1c1c", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 11, color: "#ddd", borderBottom: "0.5px solid #131313" };
const tabStyle = (active) => ({
  padding: "8px 14px", fontSize: 12, background: "transparent", border: "none", cursor: "pointer",
  borderBottom: active ? `2px solid ${NEON}` : "2px solid transparent",
  color: active ? NEON : "#888", marginBottom: -1,
  display: "inline-flex", alignItems: "center", gap: 6,
});
const badge = (color) => ({
  display: "inline-flex", alignItems: "center", gap: 3,
  padding: "1px 8px", borderRadius: 4, fontSize: 9,
  background: `${color}1a`, color, fontWeight: 500, textTransform: "capitalize",
});
const inp = {
  background: "#0a0a0a", border: "0.5px solid #2a2a2a", borderRadius: 8,
  padding: "6px 10px", fontSize: 12, color: "#fff", outline: "none", width: "100%",
};
const fieldLabel = { fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 };
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "7px 14px", fontSize: 11, borderRadius: 8,
  background: `${NEON}1f`, border: `0.5px solid ${NEON}55`,
  color: NEON, cursor: "pointer", fontWeight: 500,
};
const btnOutline = {
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "4px 10px", fontSize: 10, borderRadius: 6,
  background: "transparent", border: "0.5px solid #2a2a2a",
  color: "#bbb", cursor: "pointer",
};
